/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { assistantsEnsureManaged } from "../funcs/assistantsEnsureManaged.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Assistant } from "../models/components/assistant.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  EnsureManagedAssistantRequest,
  EnsureManagedAssistantSecurity,
} from "../models/operations/ensuremanagedassistant.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type EnsureManagedAssistantMutationVariables = {
  request?: EnsureManagedAssistantRequest | undefined;
  security?: EnsureManagedAssistantSecurity | undefined;
  options?: RequestOptions;
};

export type EnsureManagedAssistantMutationData = Assistant;

export type EnsureManagedAssistantMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * ensureManagedAssistant assistants
 *
 * @remarks
 * Get the project's built-in Project Assistant, provisioning it on first access. Idempotent — safe to call on every sidebar open.
 */
export function useEnsureManagedAssistantMutation(
  options?: MutationHookOptions<
    EnsureManagedAssistantMutationData,
    EnsureManagedAssistantMutationError,
    EnsureManagedAssistantMutationVariables
  >,
): UseMutationResult<
  EnsureManagedAssistantMutationData,
  EnsureManagedAssistantMutationError,
  EnsureManagedAssistantMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildEnsureManagedAssistantMutation(client, options),
    ...options,
  });
}

export function mutationKeyEnsureManagedAssistant(): MutationKey {
  return ["@gram/client", "assistants", "ensureManaged"];
}

export function buildEnsureManagedAssistantMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: EnsureManagedAssistantMutationVariables,
  ) => Promise<EnsureManagedAssistantMutationData>;
} {
  return {
    mutationKey: mutationKeyEnsureManagedAssistant(),
    mutationFn: function ensureManagedAssistantMutationFn({
      request,
      security,
      options,
    }): Promise<EnsureManagedAssistantMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(assistantsEnsureManaged(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
